import { z } from "zod";

import {
  getJwtToken,
  getRefreshToken,
} from "@/components/session/JwtTokenManager";

const endpoint = "/files";
const fileResponseSchema = z.object({
  user_file: z.object({
    id: z.number(),
    name: z.string(),
    size: z.number(),
    file_type: z.string(),
  }),
  url: z.string(),
});

export type UploadedFile = {
  id: string;
  name: string;
  size: number;
  type: string;
  url: string;
};

export const uploadFile =
  (options: { isPrivate: boolean }) =>
  (file: File): Promise<UploadedFile> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", file.name);
    formData.append("private", options.isPrivate.toString());
    return fetch(`${API_HOST}${endpoint}`, {
      method: "POST",
      body: formData,
      headers: {
        Authorization: `JWT ${getJwtToken()}`,
        Session: getRefreshToken() ?? "",
        IsExec: "true",
      },
    })
      .then((response) => {
        if (!response.ok) {
          return response.text().then(Promise.reject.bind(Promise));
        }
        return response.json();
      })
      .then((raw) => {
        const data = fileResponseSchema.parse(raw);
        return {
          id: data.user_file.id.toString(),
          name: data.user_file.name,
          size: data.user_file.size,
          type: data.user_file.file_type,
          url: data.url,
        };
      });
  };

export type CancellableUpload = {
  promise: Promise<UploadedFile>;
  abort: () => void;
};

export function uploadFileWithProgress({
  file,
  isPrivate,
  onProgress,
}: {
  file: File;
  isPrivate: boolean;
  onProgress?: (progress: number) => void;
}): CancellableUpload {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("name", file.name);
  formData.append("private", isPrivate.toString());

  let xhr: XMLHttpRequest;

  const promise = new Promise<UploadedFile>((resolve, reject) => {
    xhr = new XMLHttpRequest();

    // Set up progress tracking
    if (onProgress) {
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const progress = (event.loaded / event.total) * 100;
          onProgress(progress);
        }
      });
    }

    // Set up response handling
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const raw = JSON.parse(xhr.responseText);
          const data = fileResponseSchema.parse(raw);
          const url = IS_PROD ? data.url : API_HOST + data.url;
          resolve({
            id: data.user_file.id.toString(),
            name: data.user_file.name,
            size: data.user_file.size,
            type: data.user_file.file_type,
            url: url,
          });
        } catch (error) {
          reject(error);
        }
      } else {
        reject(new Error(xhr.responseText));
      }
    });

    xhr.addEventListener("error", () => {
      reject(new Error("Upload failed"));
    });

    xhr.addEventListener("abort", () => {
      reject(new Error("Upload cancelled"));
    });

    // Set up the request
    xhr.open("POST", `${API_HOST}${endpoint}`);
    xhr.setRequestHeader("Authorization", `JWT ${getJwtToken()}`);
    xhr.setRequestHeader("Session", getRefreshToken() ?? "");
    xhr.setRequestHeader("IsExec", "true");

    // Send the request
    xhr.send(formData);
  });

  return {
    promise,
    abort: () => {
      if (xhr) {
        xhr.abort();
      }
    },
  };
}
